/**
 * `BarsH` — horizontal bars, one or more series per row. The S2 Overview's two charts.
 *
 * The client drew this one. Their mockup of "Progress Against Targets" is a horizontal
 * grouped bar chart: countries down the Y axis, the value along X with ticks, two bars per
 * country (Target above Achieved), a hover popup naming the row and both numbers, and a
 * two-dot legend under the chart. This is that, in the portal's light theme rather than the
 * mockup's dark one, and it doubles as the pathway chart by being handed **one** series.
 *
 * It is a sibling of `BarsV`, not a mode of it. The two charts differ in every piece of
 * geometry they own — `BarsV` prints its category under the bar and its value above it and
 * draws no axis at all (§G8: "one axis per chart", and four printed numbers are that axis);
 * horizontal rows put the category in a left gutter, need a real X axis because a bar's
 * *length* is the only thing carrying the value, and stack N bars inside one row's band.
 * Folding both into one component would have been a prop that changes what every other prop
 * means.
 *
 * Three things it does that the mockup does not, each because this is a live page:
 *
 * 1. **A missing value draws no bar.** §D3 omits units with no data rather than reporting
 *    zero, so `null` gets an em dash where its number would be — a zero-length bar would
 *    claim the country reported nothing *because it achieved nothing*. This is the whole
 *    reason the targets chart can render at all before IFDC delivers: the Target series is
 *    all `null`, and the row is honest about it.
 * 2. **The hovered row highlights as a band**, not the single bar under the pointer. The
 *    mockup's popup names both of a country's numbers, so the thing being pointed at is the
 *    country, and highlighting half of it would be a different chart's behaviour.
 * 3. **A row is clickable** (`onSelect`), because S2 wants "click a bar → the map resizes to
 *    that country". Only when the caller passes a handler: without one the bars are not
 *    focusable and carry no pointer cursor, so the pathway chart does not pretend to be a
 *    control it is not.
 *
 * Hover goes through the shared `ChartTip` portal (§F2), like every other chart here.
 */
import { useId, useMemo, useRef, useState } from 'react';

import { NO_VALUE, fmtNum } from '../../lib/format';
import { AxisLabels } from './AxisLabels';
import { useAxisTitles } from './axisContext';
import { hideChartTip, showChartTip } from './chartTipStore';
import { useChartWidth, useChartZoom } from './useChartWidth';
import { LABEL_LINE_H, VALUE_HALO, labelColumn, placeValue, valueAxis, valueLabel } from './labels';
import { RowLabel } from './RowLabel';
import { CHART_LABEL_COL, CHART_PAD_RIGHT, axisBands } from './scale';
import { textWidth } from '../../lib/text';

/** Left gutter for the row labels, and the bottom gutter the X axis lives in. */
export const BARSH_LABEL_GUTTER = CHART_LABEL_COL;
/** Font size of a row label, and the width one character of it takes at that size. */
export const BARSH_LABEL_SIZE = 9;
export const BARSH_CHAR_RATIO = 0.52;
export const BARSH_AXIS_GUTTER = 15;
export const BARSH_PAD_TOP = 4;
export const BARSH_PAD_RIGHT = CHART_PAD_RIGHT;
/** Vertical space between two rows, and between two bars inside one row. */
export const BARSH_ROW_GAP = 7;
export const BARSH_BAR_GAP = 2;
/**
 * The mockup's `0 → 1M every 250K`: five gridlines including both ends. The values come
 * from `niceTicks`, so the axis may take a sixth when that reaches a rounder maximum.
 */
export const BARSH_TICKS = 5;
/** Headroom past the longest bar so its printed value is not clipped by the frame. */
export const BARSH_HEADROOM = 1.02;
/** Font size of a bar's printed value (mono, bold). */
export const BARSH_VALUE_SIZE = 8;
/** How far below the head of a reference line its own label's baseline sits. */
export const BARSH_REF_LABEL_H = 9;

export interface BarsHSeries {
  /** Stable id — the `data-` hook and the React key. Never shown. */
  key: string;
  /** Legend/tooltip name, already translated (`Target`, `Achieved`, `Activities`). */
  label: string;
  color: string;
  /** One per row label; `null`/non-finite = not reported (no bar, em dash). */
  values: readonly (number | null | undefined)[];
  /**
   * Optional per-row override of `color`, positionally (§G8's fixed categorical order).
   *
   * Opt-in, and only ever used by a **single**-series chart: two series each painting their
   * rows differently would leave the legend describing nothing. It exists because a chart of
   * one series per category — the pathway bars — still wants each category in its own colour,
   * and splitting it into three one-row series would give each its own axis scale.
   */
  rowColors?: readonly string[];
}

export interface BarsHProps {
  /** One per row, top to bottom — countries, watersheds or pathway names. */
  labels: readonly string[];
  /** One entry = plain bars; two = the mockup's Target/Achieved pair. */
  series: readonly BarsHSeries[];
  w?: number;
  /** Height of one row's whole band, all its bars included. */
  rowH?: number;
  /**
   * Width of the left gutter the row labels sit in, and their font size.
   *
   * `labelGutter` is a **minimum** (launch round, 7 Oct 2026): the column grows to the
   * longest measured name, up to 45 % of the chart, and a name longer than that wraps onto
   * a second line with its row grown to hold it. No name is cut with an ellipsis.
   */
  labelGutter?: number;
  labelSize?: number;
  /** Appended to every number in the tooltip (`44,858 ha`). */
  unit?: string;
  /** §G3 decimals. */
  fmt?: number;
  /** X gridlines, including 0 and the maximum. Fewer than 2 draws none. */
  ticks?: number;
  ariaLabel?: string;
  /** S2 "click a bar → the map resizes to that country". Omit and rows are inert. */
  onSelect?: (index: number, label: string) => void;
  className?: string;
  /**
   * A vertical marker across some of the rows — the targets chart's **programme reference
   * line** (client spec, 2026-09-09).
   *
   * IFDC's targets are a programme-wide commitment with no country allocation, so a country
   * row gets no target bar of its own. The line is how the reader still sees where the
   * commitment sits: it is drawn at `value`, dashed, from `fromRow` down, and it is
   * explicitly *not* a bar, because a bar on a country row would read as that country's
   * target and inventing one is the thing the specification forbids.
   *
   * It participates in the axis scale, so a line beyond every bar widens the plot rather
   * than falling off the right edge.
   */
  refLine?: { value: number; label: string; color?: string; fromRow?: number } | null;
  /**
   * **A composition instead of a comparison** — client, 15 Sep 2026: *"for the landcover we
   * can use a stacked bar plot to show all classes per country"*.
   *
   * One row is a whole (a country), the series are the parts inside it (the land-cover
   * classes), and the segments are laid end to end across the row rather than side by side.
   * The row is normalised to its **own** total, so every row fills the plot exactly and the
   * chart compares *shares* — which is the only comparison a composition supports, and the
   * reason there is no value axis in this mode: the axis is 100 %, and a gridline saying so
   * is a gridline earning nothing in a 250 px panel.
   *
   * A zero, a null and a negative are all "this row has none of that class" and draw no
   * segment. Unlike the grouped mode there is no em dash for them: a missing part of a whole
   * is already visible as the space the other parts took.
   *
   * `rowColors` still wins over `color` per row, and the per-segment popup names the series
   * and its share (`Forest · 25%`) under the row's own name.
   */
  stacked?: boolean;
  /**
   * Per-row fill opacity, positionally — the country comparison's "the filter is on some
   * other country" fade (see `InsightsPanel`'s `focusIso`).
   *
   * A row rather than a bar, because in a stack the thing being de-emphasised is the whole
   * country; painting one class of it differently would say something about the class.
   */
  rowOpacity?: readonly number[];
  /**
   * Multiplier on the longest bar (or the reference line) that sets the axis maximum.
   *
   * `BARSH_HEADROOM`'s 2 % is enough to keep a bar's printed value off the frame, which is
   * all the Overview's charts need. The dashboard's targets chart wants more: its axis is a
   * percentage with a 100 % reference line, and at 102 % the marker and the last tick are
   * pressed against the right edge (client, 2026-09-16 round 2).
   */
  headroom?: number;
  /**
   * Put each row's **untruncated** label on a native `<title>` over the label gutter.
   *
   * Opt-in, and off by default: every chart here already answers a hover through the shared
   * `ChartTip` portal, and a native tooltip on top of that is two popups for one pointer
   * (review, 2026-09-16). It earns its place only where the labels are long enough to be
   * ellipsised and the full text exists nowhere else on screen — the dashboard's targets
   * chart, whose rows are indicator names.
   */
  labelTitles?: boolean;
  /**
   * A native `<title>` per row over the label gutter, in place of the row's own label: a
   * hover sentence the label alone does not carry (UC3 Relationships' plain class
   * sentences, NOTES #760). Implies the label hit area `labelTitles` draws.
   */
  rowTitles?: readonly string[];
  /**
   * **The dashboard's softer bars** (client, 2026-09-16: "a modern look, faint colours").
   *
   * Opt-in, and off everywhere else on purpose: the Overview's and the insights panels'
   * charts are drawn in flat series colour, which is what makes a colour on one page the
   * same colour on the next. Where it is on, each bar is painted with a two-stop vertical
   * gradient of its *own* colour — full at the top, 75 % of it at the foot — and its ends
   * are rounded a pixel further. Nothing about the geometry, the scale or the numbers
   * changes; a `null` still draws no bar.
   */
  styled?: boolean;
  /** The value axis' title, "quantity (unit)", centred under the tick row. */
  xLabel?: string;
  /** The rows' title ("Country"), rotated beside the label column. */
  yLabel?: string;
}

/**
 * A compact axis label: `250K`, `1.2M`, `800` — the mockup's own register.
 *
 * Module-private on purpose. It is a property of this chart's axis, not a shared formatter,
 * and exporting it from a file that also exports a component costs a `react-refresh`
 * warning for no caller. It is asserted through the rendered tick text instead.
 */
function axisTick(value: number): string {
  const abs = Math.abs(value);
  // Two decimals for millions: a clean 1.25M step must not print as 1.3M.
  if (abs >= 1_000_000) return `${Math.round((value / 1_000_000) * 100) / 100}M`;
  if (abs >= 1_000) return `${Math.round((value / 1_000) * 10) / 10}K`;
  // Part C (11 Oct 2026): a value axis below one (NDVI 0 to 0.6) keeps its decimals, or its
  // ticks print as "0 0 0 1".
  if (abs > 0 && abs < 1) return String(Math.round(value * 100) / 100);
  return String(Math.round(value));
}

export function BarsH({
  labels,
  series,
  w: wProp = 290,
  rowH = 30,
  labelGutter = BARSH_LABEL_GUTTER,
  labelSize = BARSH_LABEL_SIZE,
  unit = '',
  fmt = 0,
  ticks = BARSH_TICKS,
  ariaLabel,
  onSelect,
  className,
  refLine = null,
  stacked = false,
  rowOpacity,
  headroom = BARSH_HEADROOM,
  labelTitles = false,
  rowTitles,
  styled = false,
  xLabel: xLabelProp,
  yLabel: yLabelProp,
}: BarsHProps) {
  /*
    #749: the chart measures its own box and lays out at that width in real pixels (its
    text stays the size it was designed at); `w` is the width where nothing can measure,
    and the lightbox's zoom (`ChartZoomContext`) is divided out, as `chartW` did.
  */
  const svgRef = useRef<SVGSVGElement>(null);
  const zoom = useChartZoom();
  const outerW = Math.max(120, useChartWidth(svgRef, wProp, { zoom }));
  const [hovered, setHovered] = useState<number | null>(null);
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  /*
    The axis titles' bands come out of the chart's own width rather than being added to it,
    so a chart given a title is drawn at the same scale as before — its plot is a band
    narrower and a band taller, and nothing else moves. A stack has no value axis to title.
  */
  const bands = axisBands(stacked ? undefined : xLabel, yLabel);
  const w = outerW - bands.left;
  /*
    One gradient per *colour*, not per bar: a chart of nine rows in one green would
    otherwise carry nine identical `<linearGradient>` elements. `useId` keeps the ids
    unique across two charts on one page; its own delimiters are stripped because the id
    is spliced into a `url(#…)` reference.
  */
  const rawId = useId();
  const gradients = useMemo(() => {
    if (!styled) return new Map<string, string>();
    const prefix = `bh${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const colors = new Set<string>();
    for (const s of series) {
      colors.add(s.color);
      for (const c of s.rowColors ?? []) colors.add(c);
    }
    return new Map([...colors].map((c, i) => [c, `${prefix}-${i}`]));
  }, [series, styled, rawId]);
  /** The bar's paint: its gradient where this chart has one, the flat colour otherwise. */
  const paint = (color: string) => {
    const id = gradients.get(color);
    return id ? `url(#${id})` : color;
  };

  const chart = useMemo(() => {
    // The label column: the longest name, between the caller's gutter and 45 % of the chart.
    const column = labelColumn(labels, { size: labelSize, min: labelGutter, total: w });
    const gutter = column.width;
    // A row grows to hold a wrapped name; its bars keep their thickness and sit centred.
    const rowHs = column.lines.map((lines) =>
      Math.max(rowH, Math.ceil(lines.length * labelSize * LABEL_LINE_H + 4)),
    );
    const rowYs = rowHs.map((_, i) => BARSH_PAD_TOP + rowHs.slice(0, i).reduce((a, b) => a + b, 0));
    const rowsH = rowHs.reduce((a, b) => a + b, 0);
    // A stack has no value axis, so it does not pay for the gutter one would live in.
    const height = BARSH_PAD_TOP + rowsH + (stacked ? BARSH_ROW_GAP : BARSH_AXIS_GUTTER);
    const plotW = Math.max(10, w - gutter - BARSH_PAD_RIGHT);
    const numbers = series.map((s) =>
      labels.map((_, i) => {
        const v = s.values[i];
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      }),
    );
    const marker =
      refLine && Number.isFinite(refLine.value) && refLine.value > 0 ? refLine.value : 0;
    // The marker is part of the scale, not drawn over it: a programme commitment larger than
    // every country's achievement would otherwise sit off the right edge of the frame.
    const peak = Math.max(0, marker, ...numbers.flat().map((v) => v ?? 0));
    /*
      The axis maximum has room for every printed figure (measured), moved past the
      reference line where it would cross it — so a figure never leaves the plot and never
      flips inside its bar. `peak > 0` guards the `v / 0` of the empty chart the targets
      chart opens in.
    */
    const items = numbers.flat().map((v) => ({
      v,
      w: v === null ? 0 : textWidth(valueLabel(v, fmt, unit), BARSH_VALUE_SIZE, 700, true),
    }));
    const axis = stacked
      ? { max: 1, ticks: [] as number[] }
      : valueAxis({
          items,
          plotW,
          refs: marker > 0 ? [marker] : [],
          floor: peak * headroom,
          steps: Math.max(1, ticks - 1),
        });
    const max = peak > 0 ? axis.max : 1;
    const scale = axis.ticks;
    const bandH = rowH - BARSH_ROW_GAP;
    const barH = Math.max(3, (bandH - BARSH_BAR_GAP * (series.length - 1)) / series.length);

    return {
      height,
      plotW,
      gutter,
      lines: column.lines,
      rowYs,
      max,
      peak,
      scale,
      bars: labels.map((label, row) => {
        // The bars' own band, centred in a row that grew for a two-line name.
        const y = rowYs[row]! + (rowHs[row]! - rowH) / 2;
        return {
        label,
        row,
        y,
        rowTop: rowYs[row]!,
        rowBand: rowHs[row]! - BARSH_ROW_GAP,
        bandH,
        /**
         * The stacked row: each series' share of *this row's* total, laid end to end.
         *
         * Normalising per row rather than against the chart's peak is what makes the row a
         * whole. `total` is the sum of the positive values only, so a row whose parts sum to
         * 99.8 after the server's rounding still fills the plot exactly rather than leaving
         * a sliver of background that reads as an unnamed class.
         */
        segments: (() => {
          if (!stacked) return [];
          const parts = series
            .map((s, si) => ({ s, v: numbers[si]?.[row] ?? null }))
            .filter((p): p is { s: BarsHSeries; v: number } => p.v !== null && p.v > 0);
          const total = parts.reduce((sum, p) => sum + p.v, 0);
          if (total <= 0) return [];
          let x = gutter;
          return parts.map((p) => {
            const width = (p.v / total) * plotW;
            const seg = { series: p.s, v: p.v, x, width, pct: (p.v / total) * 100 };
            x += width;
            return seg;
          });
        })(),
        values: series.map((s, si) => {
          const v = numbers[si]?.[row] ?? null;
          return {
            series: s,
            v,
            y: y + si * (barH + BARSH_BAR_GAP),
            h: barH,
            len: v === null ? 0 : Math.max(0, (v / max) * plotW),
            text: v === null ? NO_VALUE : valueLabel(v, fmt, unit),
          };
        }),
      };
      }),
    };
  }, [labels, series, w, rowH, labelGutter, labelSize, refLine, stacked, headroom, ticks, fmt, unit]);
  const labelGutterW = chart.gutter;

  const text = (v: number | null | undefined) =>
    v === null || v === undefined ? NO_VALUE : `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  /** The mockup's popup: `Nigeria` over `Target 800K · Achieved 612K`, in one tip. */
  const tipFor = (row: number) =>
    stacked
      ? (chart.bars[row]?.segments ?? [])
          .map((seg) => `${seg.series.label} ${fmtNum(seg.pct, fmt)}%`)
          .join(' · ')
      : (chart.bars[row]?.values ?? []).map((b) => `${b.series.label} ${text(b.v)}`).join(' · ');

  /**
   * One segment's popup: the class and its share of the row (`Forest · 25%`).
   *
   * A stacked row's whole composition can be eight classes long, so naming all of them in
   * one tip — what the grouped mode does — would print a paragraph over the map. The reader
   * is pointing at one class; the tip answers about that class.
   */
  const segmentTip = (label: string, pct: number) => `${label} · ${fmtNum(pct, fmt)}%`;

  const enter = (e: { clientX: number; clientY: number }, row: number) => {
    setHovered(row);
    showChartTip(e, labels[row] ?? '', tipFor(row));
  };

  /**
   * The same popup, placed for a row that was reached by tabbing rather than pointed at.
   *
   * `showChartTip` positions from a cursor, and a keyboard has none — so the row's own box
   * stands in for it, which puts the tip beside the row the way the pointer would.
   */
  const focusRow = (node: SVGGElement, row: number) => {
    const box = node.getBoundingClientRect();
    enter({ clientX: box.right, clientY: box.top + box.height / 2 }, row);
  };
  const leave = () => {
    setHovered(null);
    hideChartTip();
  };

  /** Where the reference line stands, or `null` when this chart has none. */
  const refX =
    refLine && Number.isFinite(refLine.value) && refLine.value > 0
      ? labelGutterW + (refLine.value / chart.max) * chart.plotW
      : null;

  // A composition's axis is 100 % — there is nothing for a gridline to say (see `stacked`).
  const tickValues = ticks >= 2 && !stacked ? chart.scale : [];

  return (
    <svg
      ref={svgRef}
      data-fluid=""
      className={`chart barsh${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${outerW} ${chart.height + bands.bottom}`}
      role="img"
      aria-label={ariaLabel ?? `${labels.length} rows`}
      data-rows={labels.length}
      data-series={series.length}
      onMouseLeave={leave}
    >
      <AxisLabels
        xLabel={stacked ? undefined : xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + labelGutterW}
        plotRight={bands.left + labelGutterW + chart.plotW}
        plotTop={BARSH_PAD_TOP}
        plotBottom={chart.height - (stacked ? BARSH_ROW_GAP : BARSH_AXIS_GUTTER)}
        height={chart.height + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined}>
      {gradients.size > 0 ? (
        <defs>
          {[...gradients].map(([color, id]) => (
            <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0.75} />
            </linearGradient>
          ))}
        </defs>
      ) : null}

      {/* Gridlines first, so every bar is drawn over them rather than under. */}
      {tickValues.map((value, i) => {
        const x = labelGutterW + (value / chart.max) * chart.plotW;
        // The axis ends where the figures need it to, which is not always on a tick.
        const atEnd = Math.abs(value - chart.max) < chart.max * 1e-9;
        return (
          <g key={`t${i}`} data-barsh-tick={i}>
            <line
              x1={x}
              x2={x}
              y1={BARSH_PAD_TOP}
              y2={chart.height - BARSH_AXIS_GUTTER}
              stroke="#E4E9E2"
              strokeWidth={1}
            />
            <text
              x={x}
              y={chart.height - 4}
              textAnchor={i === 0 ? 'start' : atEnd ? 'end' : 'middle'}
              fontSize={8}
              fill="#8B958A"
            >
              {axisTick(value)}
            </text>
          </g>
        );
      })}

      {/* The programme reference line, over the gridlines and under the bars' numbers. */}
      {refX !== null && refLine
        ? (() => {
            const from = Math.max(0, refLine.fromRow ?? 0);
            const top = chart.rowYs[from] ?? BARSH_PAD_TOP;
            return (
              <g>
                <line
                  data-barsh-refline={refLine.value}
                  data-label={refLine.label}
                  x1={refX}
                  x2={refX}
                  y1={top}
                  y2={chart.height - BARSH_AXIS_GUTTER}
                  stroke={refLine.color ?? '#1E5FA8'}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                >
                  <title>{refLine.label}</title>
                </line>
                {/*
                  The line's own words, at its head. The label used to live only in a
                  `<title>`, so a reader who never hovered was shown a dashed line with
                  nothing saying what it marks (review, 2026-09-16). It hangs to whichever
                  side has room, so it is never clipped by the frame.
                */}
                <text
                  data-barsh-reflabel={refLine.value}
                  x={refX + (refX > w * 0.7 ? -4 : 4)}
                  y={top + BARSH_REF_LABEL_H}
                  textAnchor={refX > w * 0.7 ? 'end' : 'start'}
                  fontSize={8}
                  fontWeight={700}
                  fill={refLine.color ?? '#1E5FA8'}
                  style={VALUE_HALO}
                >
                  {refLine.label}
                </text>
              </g>
            );
          })()
        : null}

      {chart.bars.map((row) => (
        /*
         * **A selectable row is reachable from the keyboard, and so is its popup.** When
         * `onSelect` is set, clicking a row moves the map (S2 item 8), which made the whole
         * gesture mouse-only — and the popup naming the row's two numbers was the only place
         * they are written down, so a reader who cannot point had neither the action nor the
         * values. Focus does what hover does and Enter/Space does what a click does; the row
         * announces itself as a button because that is what it now is.
         *
         * A row that selects nothing stays inert: it gets no tab stop, because a tab stop
         * that does nothing is worse than none.
         */
        <g
          key={row.label}
          data-barsh-row={row.row}
          data-label={row.label}
          data-hovered={hovered === row.row ? '1' : undefined}
          style={onSelect ? { cursor: 'pointer' } : undefined}
          tabIndex={onSelect ? 0 : undefined}
          role={onSelect ? 'button' : undefined}
          aria-label={onSelect ? `${row.label}, ${tipFor(row.row)}` : undefined}
          onMouseEnter={stacked ? undefined : (e) => enter(e, row.row)}
          onMouseMove={stacked ? undefined : (e) => enter(e, row.row)}
          onMouseLeave={leave}
          onFocus={onSelect ? (e) => focusRow(e.currentTarget, row.row) : undefined}
          onBlur={onSelect ? leave : undefined}
          onKeyDown={
            onSelect
              ? (e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  onSelect(row.row, row.label);
                }
              : undefined
          }
          onClick={onSelect ? () => onSelect(row.row, row.label) : undefined}
        >
          {/* The highlight band. Always rendered so the row has one hit target covering the
              label gutter, the bars and the space between them — a reader aiming at a 5px
              bar in a 300px panel should not have to. */}
          <rect
            x={0}
            y={row.rowTop - BARSH_ROW_GAP / 2}
            width={w}
            height={row.rowBand + BARSH_ROW_GAP}
            fill={hovered === row.row ? 'rgba(46,125,50,.08)' : 'transparent'}
            data-barsh-band={row.row}
          />
          <RowLabel
            lines={chart.lines[row.row] ?? [row.label]}
            x={labelGutterW - 6}
            cy={row.y + row.bandH / 2}
            size={labelSize}
            textAnchor="end"
            fill="#22301F"
            data-barsh-label={row.row}
            data-label-full={row.label}
          />
          {/* The label gutter's own hit area, carrying the name as a native tooltip as
              well. Only where the caller asked for it (`labelTitles`) — see the prop.
              Transparent and event-transparent to the row `<g>` above, so the row highlight
              and the shared `ChartTip` behave exactly as before. */}
          {labelTitles || rowTitles ? (
            <rect
              x={0}
              y={row.rowTop - BARSH_ROW_GAP / 2}
              width={labelGutterW}
              height={row.rowBand + BARSH_ROW_GAP}
              fill="transparent"
              data-barsh-label-hit={row.row}
            >
              <title>{rowTitles?.[row.row] ?? row.label}</title>
            </rect>
          ) : null}
          {stacked
            ? row.segments.map((seg) => (
                <rect
                  key={seg.series.key}
                  data-barsh-segment={`${row.row}:${seg.series.key}`}
                  data-label={seg.series.label}
                  data-value={segmentTip(seg.series.label, seg.pct)}
                  x={seg.x}
                  y={row.y}
                  width={seg.width}
                  height={row.bandH}
                  fill={paint(seg.series.rowColors?.[row.row] ?? seg.series.color)}
                  opacity={
                    (rowOpacity?.[row.row] ?? 1) *
                    (hovered === null || hovered === row.row ? 1 : 0.55)
                  }
                  onMouseEnter={(e) => {
                    setHovered(row.row);
                    showChartTip(e, row.label, segmentTip(seg.series.label, seg.pct));
                  }}
                  onMouseMove={(e) => {
                    setHovered(row.row);
                    showChartTip(e, row.label, segmentTip(seg.series.label, seg.pct));
                  }}
                  onMouseLeave={leave}
                />
              ))
            : null}
          {stacked ? null : row.values.map((bar) => {
            const end = labelGutterW + bar.len;
            /*
              The figure always sits outside the bar, past its end, on a halo of the panel's
              paper; where the reference line would run through it, it moves past the line
              (launch round, 7 Oct 2026). The axis was sized for it, so it never leaves the
              plot and never has to flip inside the bar.
            */
            const textW = textWidth(bar.text, BARSH_VALUE_SIZE, 700, true);
            const valueX = placeValue(end, textW, refX === null ? [] : [refX]);
            return (
              <g key={bar.series.key} data-barsh-series={bar.series.key}>
                {bar.v === null ? null : (
                  <rect
                    x={labelGutterW}
                    y={bar.y}
                    width={bar.len}
                    height={bar.h}
                    rx={styled ? 3 : 2}
                    fill={paint(bar.series.rowColors?.[row.row] ?? bar.series.color)}
                    opacity={
                      (rowOpacity?.[row.row] ?? 1) *
                      (hovered === null || hovered === row.row ? 1 : 0.55)
                    }
                    data-value={text(bar.v)}
                  />
                )}
                <text
                  x={valueX}
                  y={bar.y + bar.h / 2 + 3}
                  textAnchor="start"
                  fontSize={BARSH_VALUE_SIZE}
                  fontWeight={700}
                  fontFamily="var(--mono)"
                  fill="#22301F"
                  style={VALUE_HALO}
                  data-barsh-value={`${row.row}:${bar.series.key}`}
                >
                  {bar.text}
                </text>
              </g>
            );
          })}
        </g>
      ))}
      </g>
    </svg>
  );
}

export default BarsH;
